<template lang="pug">
    v-container(fluid grid-list-md)
        v-overlay(:value="deleting")
            v-progress-circular(indeterminate size="64")
        v-layout(wrap align-center)
            page-title(:title="$t('pages.settings.company.title')" :icon="pageIcons.company")
            search-box(v-model="search" :loading="loading" @search="onSearch" @setting="showAdvanceSearchDialog"
                :search-label="$t('pages.settings.company.labelSearch')"
                :advance-search-hint="$t('pages.settings.company.searchConfigHint')")
            add-new-button(:caption="$t('pages.settings.company.buttonAdd')" :disabled="loading" @click="newItem")

            v-flex(xs12 tag="v-card" mt-2)
                v-data-table(:headers="headers" :items="items" :mobile-breakpoint="50"
                    :footer-props="{ showFirstLastPage: true, showCurrentPage: true }" @update:options="onChangeOptions"
                    :loading="loading" :options.sync="options" :server-items-length="total" multi-sort).table
                    template(#top)
                        v-layout(column mx-auto pa-3)
                            .body-2 ผลลัพธ์ <b>{{total}}</b> รายการ&nbsp;&nbsp;
                                span(v-if="keywords") คำค้น&nbsp;&nbsp;
                                    v-chip.mr-2.font-weight-medium.primary--text(close @click:close="onRemoveKeywordChip") {{keywords}}
                                span(v-if="sortDescKeys.length") เรียงตาม&nbsp;&nbsp;
                                    v-chip.mr-2.font-weight-medium.primary--text(v-for="column in sortDescKeys" close @click:close="onRemoveSortChip(column)").my-1
                                        v-icon(left @click="onToggleSort(column)") {{sortDesc[column] ? 'mdi-arrow-down-circle-outline' : 'mdi-arrow-up-circle-outline'}}
                                        span {{$t(`pages.settings.company.table.headers.${column}.title`)}}
                                span(v-if="searchBy.length") ค้นหาจาก&nbsp;&nbsp;
                                    v-chip.mr-2.font-weight-medium.primary--text(v-for="column in searchBy" close @click:close="onRemoveSearchChip(column)").my-1
                                        span {{$t(`pages.settings.company.table.headers.${column}.title`)}}
                            .body-2(v-if="searchForType.length || searchForStatus.length") ค้นหาจาก
                                span(v-if="searchForType.length") ประเภท&nbsp;&nbsp;
                                    v-chip.mr-2.font-weight-medium.primary--text(v-for="type in searchForType" close @click:close="onRemoveTypeChip(type)").my-1
                                        span {{type}}
                                span(v-if="searchForStatus.length") สถานะ&nbsp;&nbsp;
                                    v-chip.mr-2.font-weight-medium(v-for="status in searchForStatus" :color="status ? 'green' : 'red'"  dark close @click:close="onRemoveStatusChip(status)").my-1
                                        v-icon(left) {{status ? 'mdi-check' : 'mdi-cancel'}}
                                        span {{$t(`pages.settings.company.table.headers.status.values[${status}]`)}}
                    template(#item.code='{ value }')
                        highlighter(:text="value" :keyword="keywords" v-if="searchBy.length === 0 || searchBy.includes('code')")
                        span(v-else) {{value}}
                    template(#item.name='{ value }')
                        highlighter(:text="value" :keyword="keywords" v-if="searchBy.length === 0 || searchBy.includes('name')")
                        span(v-else) {{value}}
                    template(#item.status='{ value }')
                        v-chip(:color="value ? 'green' : 'red'" dark)
                            v-icon(left) {{value ? 'mdi-check' : 'mdi-cancel'}}
                            | {{$t(`pages.settings.company.table.headers.status.values[${value}]`)}}
                    template(#item.types='{ value }')
                        v-chip(v-for="type in value").mr-1 {{type}}
                    template(#item.action='{ item }')
                        v-layout(pa-0 ma-0)
                            v-btn(icon @click='editItem(item)')
                                v-icon mdi-pencil-outline
                            v-btn(icon @click='deleteItem(item)')
                                v-icon mdi-delete-outline
        advanced-search-dialog(ref="refAdvancedSearchDialog" @submit="onSearchAdvanceSubmit"
            :search-columns-label="$t('pages.settings.company.searchConfigColumns')"
            :search-types-label="$t('pages.settings.company.table.headers.type.title')"
            :search-statuses-label="$t('pages.settings.company.table.headers.status.title')"
            :title="$t('pages.settings.company.searchConfigHint')")
        edit-dialog(ref="refEditDialog" @saved="onSaved")
</template>

<script>
import { sync, get, call } from "vuex-pathify";
import _ from "lodash";
import auth from "../../mixins/auth";
import SearchBox from "../../components/commons/SearchBox";
import PageTitle from "../../components/commons/PageTitle";
import AddNewButton from "../../components/commons/AddNewButton";
import AdvancedSearchDialog from "../../components/company/AdvancedSearchDialog";
import EditDialog from "../../components/company/EditDialog";
import crud from "../../mixins/crud";
import general from "../../mixins/general";
import { ensureCompanyTheme } from "../../services";

export default {
  name: "CompanySetting",
  components: {
    EditDialog,
    AdvancedSearchDialog,
    AddNewButton,
    PageTitle,
    SearchBox
  },
  mixins: [auth, crud, general],
  data() {
    return {
      deleting: false
    };
  },
  computed: {
    search: sync("companies/search"),
    options: sync("companies/options"),
    loading: get("companies/loading"),
    loaded: get("companies/loaded"),
    items: get("companies/items"),
    total: get("companies/total"),

    keywords: get("companies/keywords"),
    searchBy: get("companies/searchBy"),
    sortDesc: get("companies/sortDesc"),
    sortDescKeys: get("companies/sortDescKeys"),
    searchForType: get("companies/searchForType"),
    searchForStatus: get("companies/searchForStatus"),
    headers: {
      get() {
        return [
          {
            text: this.$t("pages.settings.company.table.headers.row.title"),
            align: "left",
            sortable: false,
            value: "row",
            class: ["body-2"]
          },
          {
            text: this.$t("pages.settings.company.table.headers.code.title"),
            align: "left",
            sortable: true,
            value: "code",
            class: ["body-2"]
          },
          {
            text: this.$t("pages.settings.company.table.headers.name.title"),
            value: "name",
            class: ["body-2"]
          },
          // {
          //     text: this.$t("pages.settings.company.table.headers.type.title"),
          //     value: "type",
          //     class: ["body-2"]
          // },
          {
            text: this.$t("pages.settings.company.table.headers.type.title"),
            value: "types",
            sortable: false,
            class: ["body-2"]
          },
          {
            text: this.$t("pages.settings.company.table.headers.status.title"),
            value: "status",
            class: ["body-2"]
          },
          {
            text: this.$t("pages.settings.company.table.headers.actions.title"),
            value: "action",
            class: ["body-2"],
            sortable: false
          }
        ];
      }
    }
  },
  methods: {
    load: call("companies/load"),
    reload: call("companies/reload"),
    onSearch() {
      // console.log("onSearchEnter");
      if (!this.loading)
        this.load({
          search: this.search,
          options: this.options,
          searchBy: this.searchBy,
          searchForType: this.searchForType,
          searchForStatus: this.searchForStatus
        });
    },
    onChangeOptions(options) {
      // console.log("onChangeOptions");
      if (this.loaded && !this.loading) {
        this.load({
          search: this.keywords,
          options,
          searchBy: this.searchBy,
          searchForType: this.searchForType,
          searchForStatus: this.searchForStatus
        });
      }
    },
    onRemoveKeywordChip() {
      // console.log("onRemoveKeywordChip");
      if (this.loaded && !this.loading) {
        this.load({
          search: "",
          options: this.options,
          searchBy: this.searchBy,
          searchForType: this.searchForType,
          searchForStatus: this.searchForStatus
        });
      }
    },
    onRemoveSortChip(column) {
      // console.log("onRemoveSortChip");
      const index = _.indexOf(this.options.sortBy, column);
      const options = _.cloneDeep(this.options);
      if (index > -1 && this.loaded && !this.loading) {
        options.sortBy.splice(index, 1);
        options.sortDesc.splice(index, 1);
        this.options = options;
      }
    },
    onRemoveSearchChip(column) {
      // console.log("onRemoveSearchChip");
      const searchBy = _.cloneDeep(this.searchBy);
      const index = _.indexOf(searchBy, column);
      if (index > -1 && !this.loading) {
        searchBy.splice(index, 1);
        this.load({
          search: this.search,
          options: this.options,
          searchBy: searchBy,
          searchForType: this.searchForType,
          searchForStatus: this.searchForStatus
        });
      }
    },
    onRemoveTypeChip(type) {
      // console.log("onRemoveTypeChip");
      const searchForType = _.cloneDeep(this.searchForType);
      const index = _.indexOf(searchForType, type);
      if (index > -1 && !this.loading) {
        searchForType.splice(index, 1);
        this.load({
          search: this.search,
          options: this.options,
          searchBy: this.searchBy,
          searchForType: searchForType,
          searchForStatus: this.searchForStatus
        });
      }
    },
    onRemoveStatusChip(status) {
      // console.log("onRemoveStatusChip");
      const searchForStatus = _.cloneDeep(this.searchForStatus);
      const index = _.indexOf(searchForStatus, status);
      if (index > -1 && !this.loading) {
        searchForStatus.splice(index, 1);
        this.load({
          search: this.search,
          options: this.options,
          searchBy: this.searchBy,
          searchForType: this.searchForType,
          searchForStatus: searchForStatus
        });
      }
    },
    onToggleSort(column) {
      // console.log("onToggleSort");
      const index = _.indexOf(this.options.sortBy, column);
      const options = _.cloneDeep(this.options);
      if (index > -1 && this.loaded && !this.loading) {
        options.sortDesc[index] = !options.sortDesc[index];
        this.options = options;
      }
    },
    showAdvanceSearchDialog() {
      // console.log("showAdvanceSearchDialog");

      this.$refs.refAdvancedSearchDialog.selectedColumns = _.cloneDeep(
        this.searchBy
      );
      this.$refs.refAdvancedSearchDialog.selectedTypes = _.cloneDeep(
        this.searchForType
      );
      this.$refs.refAdvancedSearchDialog.selectedStatuses = _.cloneDeep(
        this.searchForStatus
      );

      this.$refs.refAdvancedSearchDialog.show();
    },
    onSearchAdvanceSubmit({
      selectedColumns,
      selectedTypes,
      selectedStatuses
    }) {
      // console.log("onSearchAdvanceSubmit");
      if (!this.loading)
        this.load({
          search: this.search,
          options: this.options,
          searchBy: selectedColumns,
          searchForType: selectedTypes,
          searchForStatus: selectedStatuses
        });
    },
    newItem() {
      this.$refs.refEditDialog.show("create");
    },
    editItem(item) {
      this.$refs.refEditDialog.show("edit", item);
    },
    deleteItem(item) {
      this.$confirm(
        this.$t("messages.deleteCompanyConfirmation").printf(item)
      ).then(value => {
        if (value) {
          this.deleting = true;
          setTimeout(() => {
            this.axios
              .delete(`companies/${encodeURIComponent(item.code)}`)
              .then(() => {
                this.deleting = false;
                this.$toasted.info(
                  this.$t("general.deleted") + ` "${item.name}"`,
                  { icon: "check" }
                );
                this.reload();
              })
              .catch(error => {
                this.deleting = false;
                this.deleteErrorHandler(error).then(() => this.reload());
              });
          }, 500);
        }
      });
    },
    onSaved(company) {
      this.$toasted.success(this.$t("general.saved") + ` "${company.name}"`, {
        icon: "check"
      });
      this.reload(undefined);
      this.$store.dispatch("auth/fetchUser").then(user => {
        ensureCompanyTheme(user);
      });
    }
  },
  mounted() {
    if (!this.loading && !this.loaded) {
      this.load();
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.authenticated) {
      if (this.$refs.refAdvancedSearchDialog.showing) {
        this.$refs.refAdvancedSearchDialog.close();
        return next(false);
      } else if (this.deleting || this.$refs.refEditDialog.saving) {
        return next(false);
      } else if (this.$refs.refEditDialog.showing) {
        this.$refs.refEditDialog.close();
        return next(false);
      }
    }
    next();
  }
};
</script>

<style scoped>
.table >>> td,
th {
  /*width: 50%;*/
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
